import React from 'react';
import { Button } from '@/components/ui/button';
import { X, Waves, Music, Headphones, Key, Sparkles, Volume2, Clock } from 'lucide-react';

const HelpManual = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-xl max-w-4xl w-full mx-4 border border-purple-500/30 my-8">
        <div className="sticky top-0 bg-gradient-to-r from-purple-900 to-gray-900 p-6 rounded-t-xl border-b border-purple-500/30 flex justify-between items-center">
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            📖 NeuroWave One User Manual
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        <div className="p-6 space-y-8 max-h-[70vh] overflow-y-auto">
          
          {/* Introduction */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Waves className="w-6 h-6 text-purple-400" />
              Welcome to NeuroWave One
            </h3>
            <p className="text-white/80 text-sm leading-relaxed">
              NeuroWave One is a powerful brainwave entrainment tool that uses binaural beats and spatial audio to help you achieve desired mental states. Whether you need to focus, relax, meditate, or sleep, this app guides your brainwaves to optimal frequencies.
            </p>
          </section>

          {/* Getting Started */}
          <section className="bg-blue-500/10 border border-blue-400/20 p-5 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              🚀 Quick Start Guide
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-white/80 text-sm">
              <li><strong className="text-white">Select a Preset</strong> - Choose from Free, Premium, or create your own</li>
              <li><strong className="text-white">Set Volume</strong> - Adjust the binaural beat volume to a comfortable level</li>
              <li><strong className="text-white">Press Play</strong> - Click the ▶ button to start your session</li>
              <li><strong className="text-white">Add Background Audio (Optional)</strong> - Upload an MP3 for ambient sound</li>
              <li><strong className="text-white">Enable 8D Audio (Premium)</strong> - For immersive spatial audio experience</li>
            </ol>
          </section>

          {/* Binaural Beats */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Waves className="w-6 h-6 text-cyan-400" />
              Understanding Binaural Beats
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Binaural beats work by playing two slightly different frequencies in each ear. Your brain perceives the difference as a "beat" that can influence your brainwave patterns.
            </p>
            
            <div className="grid gap-4">
              <div className="bg-red-500/10 border border-red-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  🔴 Delta Waves (0.5-4 Hz)
                </h4>
                <p className="text-white/70 text-sm"><strong>Use For:</strong> Deep sleep, healing, pain relief</p>
                <p className="text-white/60 text-xs mt-1">Delta waves are associated with the deepest levels of relaxation and restorative sleep.</p>
              </div>

              <div className="bg-purple-500/10 border border-purple-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  🟣 Theta Waves (4-8 Hz)
                </h4>
                <p className="text-white/70 text-sm"><strong>Use For:</strong> Meditation, creativity, REM sleep, intuition</p>
                <p className="text-white/60 text-xs mt-1">Theta waves occur during deep meditation, light sleep, and enhanced creativity.</p>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  🔵 Alpha Waves (8-14 Hz)
                </h4>
                <p className="text-white/70 text-sm"><strong>Use For:</strong> Relaxation, stress reduction, light meditation</p>
                <p className="text-white/60 text-xs mt-1">Alpha waves create a calm, alert state perfect for relaxation and mindfulness.</p>
              </div>

              <div className="bg-green-500/10 border border-green-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  🟢 Beta Waves (14-30 Hz)
                </h4>
                <p className="text-white/70 text-sm"><strong>Use For:</strong> Focus, concentration, active thinking, problem-solving</p>
                <p className="text-white/60 text-xs mt-1">Beta waves dominate during waking consciousness and active mental engagement.</p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  🟡 Gamma Waves (30+ Hz)
                </h4>
                <p className="text-white/70 text-sm"><strong>Use For:</strong> Peak focus, cognitive enhancement, heightened perception</p>
                <p className="text-white/60 text-xs mt-1">Gamma waves are associated with high-level information processing and peak mental states.</p>
              </div>
            </div>
          </section>

          {/* Timeline Feature */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Clock className="w-6 h-6 text-purple-400" />
              Interactive Timeline
            </h3>
            <p className="text-white/80 text-sm mb-3">
              The timeline shows your session progress and frequency changes over time.
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/80 text-sm">
              <li><strong className="text-white">Drag the Indicator</strong> - Click and drag (or touch) to jump to any point in your session</li>
              <li><strong className="text-white">Color Coding</strong> - Each frequency range has a unique color on the timeline</li>
              <li><strong className="text-white">Smooth Transitions</strong> - The app gradually transitions between frequencies for comfort</li>
            </ul>
          </section>

          {/* Presets */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-gold-400" />
              Working with Presets
            </h3>
            <div className="space-y-4">
              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2">📁 Free Presets</h4>
                <p className="text-white/70 text-sm">
                  Pre-configured sessions available to all users. Perfect for getting started.
                </p>
              </div>

              <div className="bg-gold-500/10 border border-gold-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2">⭐ Premium Presets</h4>
                <p className="text-white/70 text-sm mb-2">
                  Advanced sessions designed for specific goals. Requires premium access.
                </p>
                <p className="text-white/60 text-xs">
                  Unlock with a 22-day free trial or permanent access key.
                </p>
              </div>

              <div className="bg-purple-500/10 border border-purple-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2">✏️ Custom Presets</h4>
                <p className="text-white/70 text-sm mb-3">
                  Create your own sessions by customizing frequency stages.
                </p>
                <p className="text-white/80 text-sm"><strong>To Create:</strong></p>
                <ol className="list-decimal list-inside space-y-1 text-white/70 text-sm mt-2">
                  <li>Click "Create Custom Preset" button</li>
                  <li>Set frequency and duration for each stage</li>
                  <li>Name and describe your preset</li>
                  <li>Save to your library</li>
                </ol>
                <p className="text-white/60 text-xs mt-3">
                  💡 <strong>Tip:</strong> Start with low frequencies (Delta/Theta) and gradually increase, or reverse for sleep sessions.
                </p>
              </div>
            </div>
          </section>

          {/* Background Audio */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Music className="w-6 h-6 text-blue-400" />
              Background Audio
            </h3>
            <p className="text-white/80 text-sm mb-3">
              Add your favorite ambient sounds, nature recordings, or music to enhance your session.
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/80 text-sm">
              <li><strong className="text-white">Supported Formats:</strong> MP3, WAV, M4A, and most audio formats</li>
              <li><strong className="text-white">Volume Control:</strong> Independent volume slider for background audio</li>
              <li><strong className="text-white">Loop Playback:</strong> Background audio automatically loops for continuous ambiance</li>
              <li><strong className="text-white">Mixing:</strong> Background audio mixes perfectly with binaural beats</li>
            </ul>
          </section>

          {/* Live Audio Input */}
          <section className="bg-purple-500/10 border border-purple-400/20 p-5 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Headphones className="w-6 h-6 text-purple-400" />
              Live Audio Input (Desktop)
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Capture and process live system audio from your DAW, Spotify, YouTube, or any application through the 8D spatial audio engine.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-white font-bold mb-2">🔧 Setup Required</h4>
                <p className="text-white/70 text-sm mb-2">
                  You need a virtual audio loopback device to route system audio into the app:
                </p>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm ml-4">
                  <li><strong>macOS:</strong> BlackHole (free)</li>
                  <li><strong>Windows:</strong> VB-Cable (free)</li>
                  <li><strong>Linux:</strong> PulseAudio Loopback (built-in)</li>
                </ul>
                <p className="text-white/60 text-xs mt-2">
                  💡 Click the "Setup Help" button for detailed installation instructions.
                </p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-2">🎚️ Features</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>Real-time audio level meter with visual feedback</li>
                  <li>Mix live input with MP3 background audio simultaneously</li>
                  <li>8D spatial effects apply to live audio when enabled</li>
                  <li>Change input device on-the-fly (🔄 button)</li>
                  <li>Independent volume control via background volume slider</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-bold mb-2">🎮 How to Use</h4>
                <ol className="list-decimal list-inside space-y-1 text-white/70 text-sm">
                  <li>Set up your loopback device (see Setup Help)</li>
                  <li>Click "⚙️ Audio Device Settings" to configure input/output</li>
                  <li><strong>Input Device:</strong> Select your loopback device (BlackHole/VB-Cable)</li>
                  <li><strong>Output Device:</strong> Select your headphones/speakers</li>
                  <li>Click "🎤 Enable Live Input"</li>
                  <li>Audio level meter shows input signal</li>
                  <li>Enable 8D audio for spatial processing</li>
                  <li>Click 🔊 button to monitor live audio (hear processed audio in real-time)</li>
                </ol>
                <div className="bg-green-500/10 border border-green-400/20 p-3 rounded-lg mt-3">
                  <p className="text-green-200 text-xs">
                    <strong>💡 Pro Tip:</strong> With device selection, you can route audio from any app (Spotify, DAW, etc.) through BlackHole → NeuroWave processes it with 8D effects → outputs to your headphones. No feedback loops!
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 8D Spatial Audio */}
          <section className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-400/20 p-5 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-pink-400" />
              8D Spatial Audio (Premium)
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Immersive spatial audio that creates a 3D sound environment around your head. Requires premium access.
            </p>

            <div className="space-y-4">
              <div>
                <h4 className="text-white font-bold mb-2">🎛️ Controls</h4>
                <ul className="list-disc list-inside space-y-2 text-white/70 text-sm">
                  <li><strong className="text-white">Panning Path:</strong> Choose how sound moves (Circle, Figure-8, Random, Vertical, or Horizontal)</li>
                  <li><strong className="text-white">Speed:</strong> Control how fast the sound moves around you (0.1x - 5x)</li>
                  <li><strong className="text-white">Radius:</strong> Set the distance of sound from your head (1-15 meters)</li>
                  <li><strong className="text-white">Reverb Mix:</strong> Add spaciousness with reverb (0-100%)</li>
                </ul>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-400/20 p-3 rounded">
                <p className="text-yellow-200 text-xs">
                  <strong>⚠️ Important:</strong> Use headphones for best 8D spatial audio experience. Speakers won't produce the 3D effect.
                </p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-2">🎯 Best Use Cases</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>Deep meditation with immersive soundscapes</li>
                  <li>Focus sessions with moving spatial cues</li>
                  <li>Creative work with inspiring audio movement</li>
                  <li>Processing DAW output with spatial enhancement</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Premium Access */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Key className="w-6 h-6 text-gold-400" />
              Premium Features & Access
            </h3>
            <div className="space-y-4">
              <div className="bg-gold-500/10 border border-gold-400/20 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-2">✨ Premium Includes:</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>8D Spatial Audio Effects</li>
                  <li>Premium Presets Library</li>
                  <li>Save Custom Presets</li>
                  <li>MP3 Export</li>
                  <li>Session History</li>
                  <li>Advanced Analytics</li>
                  <li>Priority Support</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-bold mb-2">🎁 Free Trial</h4>
                <p className="text-white/70 text-sm">
                  Get <strong className="text-white">22 days</strong> of full premium access, completely free! No credit card required.
                </p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-2">🔑 Access Keys</h4>
                <p className="text-white/70 text-sm mb-2">
                  Purchase a permanent access key for lifetime premium access.
                </p>
                <p className="text-white/60 text-xs">
                  Enter your key in the Premium section to activate.
                </p>
              </div>
            </div>
          </section>

          {/* Tips & Best Practices */}
          <section className="bg-green-500/10 border border-green-400/20 p-5 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-3">
              💡 Tips & Best Practices
            </h3>
            <div className="space-y-3 text-white/80 text-sm">
              <div>
                <strong className="text-white block mb-1">🎧 Always Use Headphones</strong>
                <p className="text-white/70">Binaural beats require headphones to work properly. Speakers won't create the necessary stereo separation.</p>
              </div>

              <div>
                <strong className="text-white block mb-1">🔊 Start with Lower Volumes</strong>
                <p className="text-white/70">Begin at 30-40% volume and adjust to your comfort level. Binaural beats don't need to be loud to be effective.</p>
              </div>

              <div>
                <strong className="text-white block mb-1">⏰ Session Duration</strong>
                <p className="text-white/70">Most effective sessions are 15-30 minutes. Longer isn't always better - consistency matters more.</p>
              </div>

              <div>
                <strong className="text-white block mb-1">🧘 Minimize Distractions</strong>
                <p className="text-white/70">Find a quiet space and remove distractions for maximum effectiveness.</p>
              </div>

              <div>
                <strong className="text-white block mb-1">📊 Track Your Progress</strong>
                <p className="text-white/70">Note which presets and frequencies work best for you. Everyone responds differently.</p>
              </div>

              <div>
                <strong className="text-white block mb-1">🚫 When NOT to Use</strong>
                <p className="text-white/70">Avoid while driving, operating machinery, or if you have epilepsy or are prone to seizures.</p>
              </div>
            </div>
          </section>

          {/* Keyboard Shortcuts */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3">
              ⌨️ Keyboard Shortcuts
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 p-3 rounded-lg">
                <div className="text-white font-mono font-bold">Space</div>
                <div className="text-white/70 text-sm">Play / Pause</div>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <div className="text-white font-mono font-bold">S</div>
                <div className="text-white/70 text-sm">Stop Session</div>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-3">
              🔧 Troubleshooting
            </h3>
            <div className="space-y-3">
              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-1">No Sound?</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>Check that your headphones are properly connected</li>
                  <li>Ensure volume sliders are not at 0%</li>
                  <li>Try refreshing the page</li>
                  <li>Check browser audio permissions</li>
                </ul>
              </div>

              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-1">8D Audio Not Working?</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>Ensure you have premium access</li>
                  <li>Use headphones (required for spatial audio)</li>
                  <li>Try disabling and re-enabling 8D audio</li>
                  <li>Reload the background audio file</li>
                </ul>
              </div>

              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-bold mb-1">Live Input Issues?</h4>
                <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
                  <li>Verify loopback device is installed (BlackHole/VB-Cable)</li>
                  <li>Check system audio routing settings</li>
                  <li>Grant browser microphone permissions</li>
                  <li>Try changing input device (🔄 button)</li>
                  <li>Check audio level meter for activity</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact & Support */}
          <section className="bg-blue-500/10 border border-blue-400/20 p-5 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-3">
              📧 Need Help?
            </h3>
            <p className="text-white/80 text-sm mb-3">
              If you encounter any issues or have questions not covered in this manual, we're here to help!
            </p>
            <div className="space-y-2 text-white/70 text-sm">
              <p>📖 This manual covers all current features</p>
              <p>🔄 Check for app updates regularly</p>
              <p>💬 Premium users get priority support</p>
            </div>
          </section>

        </div>

        <div className="sticky bottom-0 bg-gradient-to-r from-gray-900 to-purple-900 p-4 rounded-b-xl border-t border-purple-500/30">
          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            Close Manual
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpManual;
